/**
 * AgentAvatarPage
 * Admin page for managing default agent avatars.
 *
 * Features:
 * - Stats bar (total, active, image, video counts)
 * - Filter bar: status, category, type + search by name (debounced)
 * - Responsive grid of AvatarCards
 * - Upload dialog (multipart/form-data to POST /v1/admin/agent-avatars)
 * - Edit dialog (PATCH /v1/admin/agent-avatars/:id)
 * - Toggle active/inactive (PATCH /v1/admin/agent-avatars/:id/status)
 * - Permanent delete with confirmation (DELETE /v1/admin/agent-avatars/:id)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from '@mui/material';
import { Image as ImageIcon, Plus, Search } from 'lucide-react';
import { useNotification } from '../../../../hooks/useNotification';
import {
  useAvatars,
  useUploadAvatar,
  useUpdateAvatar,
  useToggleAvatarStatus,
  useDeleteAvatar,
} from '../../../../services/agentAvatar.service';
import { AvatarCard } from '../components/AvatarCard';
import { AvatarUploadDialog } from '../components/AvatarUploadDialog';
import { AvatarEditDialog } from '../components/AvatarEditDialog';
import { DefaultAgentProfileCard } from '../components/DefaultAgentProfileCard';
import type {
  AgentAvatar,
  AvatarCategory,
  AvatarStatus,
  AvatarType,
  UploadAvatarPayload,
  UpdateAvatarPayload,
} from '../types';

// ============================================
// Debounce hook
// ============================================

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ============================================
// Stat card
// ============================================

interface StatCardProps {
  label: string;
  value: number | string;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color = '#3B82F6' }) => (
  <Box
    className="flex flex-col items-center justify-center rounded-xl p-4 shadow-sm"
    sx={{
      bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
      border: '1px solid',
      borderColor: (theme) =>
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    }}
  >
    <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
      {value}
    </Typography>
    <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
      {label}
    </Typography>
  </Box>
);

// ============================================
// Main Page
// ============================================

const PAGE_SIZE = 20;

export const AgentAvatarPage: React.FC = () => {
  const notification = useNotification();

  // ---- Filter state ----
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 350);
  const [statusFilter, setStatusFilter] = useState<AvatarStatus>('all');
  const [categoryFilter, setCategoryFilter] = useState<AvatarCategory | ''>('');
  const [typeFilter, setTypeFilter] = useState<AvatarType | ''>('');
  const [page, setPage] = useState(1);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, categoryFilter, typeFilter]);

  // ---- API query params ----
  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status: statusFilter,
      category: categoryFilter || undefined,
      type: typeFilter || undefined,
    }),
    [page, statusFilter, categoryFilter, typeFilter]
  );

  const { data, isLoading, isError } = useAvatars(queryParams);

  // ---- Client-side name search (API doesn't expose search; filter locally) ----
  const avatars: AgentAvatar[] = useMemo(() => {
    const list = data?.data ?? [];
    if (!debouncedSearch.trim()) return list;
    const q = debouncedSearch.toLowerCase();
    return list.filter((a) => a.name.toLowerCase().includes(q));
  }, [data, debouncedSearch]);

  const meta = data?.meta;

  // ---- Stats derived from current page data ----
  const allData = data?.data ?? [];
  const activeCount = allData.filter((a) => a.isActive).length;
  const imageCount = allData.filter((a) => a.type === 'image').length;
  const videoCount = allData.filter((a) => a.type === 'video').length;

  // ---- Dialogs ----
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editAvatar, setEditAvatar] = useState<AgentAvatar | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // ---- Mutations ----
  const uploadMutation = useUploadAvatar();
  const updateMutation = useUpdateAvatar();
  const toggleMutation = useToggleAvatarStatus();
  const deleteMutation = useDeleteAvatar();

  // ---- Handlers ----

  const handleUpload = async (payload: UploadAvatarPayload) => {
    try {
      await uploadMutation.mutateAsync(payload);
      notification.success('Avatar uploaded successfully');
      setUploadOpen(false);
    } catch {
      notification.error('Failed to upload avatar');
    }
  };

  const handleEdit = async (payload: UpdateAvatarPayload) => {
    try {
      await updateMutation.mutateAsync(payload);
      notification.success('Avatar updated successfully');
      setEditAvatar(null);
    } catch {
      notification.error('Failed to update avatar');
    }
  };

  const handleToggleStatus = async (publicId: string, isActive: boolean) => {
    try {
      await toggleMutation.mutateAsync({ publicId, isActive });
      notification.success(`Avatar ${isActive ? 'activated' : 'deactivated'}`);
    } catch {
      notification.error('Failed to update avatar status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget);
      notification.success('Avatar deleted successfully');
    } catch {
      notification.error('Failed to delete avatar');
    } finally {
      setDeleteTarget(null);
    }
  };

  const hasActiveFilters = searchInput || statusFilter !== 'all' || categoryFilter || typeFilter;

  const handleClearFilters = () => {
    setSearchInput('');
    setStatusFilter('all');
    setCategoryFilter('');
    setTypeFilter('');
  };

  // ============================================
  // Render
  // ============================================

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* ---- Page header ---- */}
      <Box className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <Box>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            Agent Avatars
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
            Upload and manage default avatars for AI agents
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={() => setUploadOpen(true)}
          sx={{
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563EB' },
            textTransform: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            alignSelf: 'flex-start',
          }}
        >
          Upload Avatar
        </Button>
      </Box>

      {/* ---- Stats row ---- */}
      <Box className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total (this page)" value={allData.length} color="#3B82F6" />
        <StatCard label="Active" value={activeCount} color="#10B981" />
        <StatCard label="Images" value={imageCount} color="#8B5CF6" />
        <StatCard label="Videos" value={videoCount} color="#F59E0B" />
      </Box>

      {/* ---- Default agent of new users ---- */}
      <DefaultAgentProfileCard />

      {/* ---- Filter bar ---- */}
      <Box className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        {/* Search */}
        <Box className="relative flex-1">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500"
          />
          <input
            type="text"
            placeholder="Search by name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder-slate-400"
          />
        </Box>

        {/* Status filter */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel
            sx={{ color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) }}
          >
            Status
          </InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as AvatarStatus)}
            sx={{
              color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined),
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : undefined),
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        {/* Category filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel
            sx={{ color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) }}
          >
            Category
          </InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value as AvatarCategory | '')}
            sx={{
              color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined),
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : undefined),
            }}
          >
            <MenuItem value="">All Categories</MenuItem>
            <MenuItem value="robot">Robot</MenuItem>
            <MenuItem value="anime">Anime</MenuItem>
            <MenuItem value="human">Human</MenuItem>
            <MenuItem value="abstract">Abstract</MenuItem>
          </Select>
        </FormControl>

        {/* Type filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel
            sx={{ color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) }}
          >
            Type
          </InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as AvatarType | '')}
            sx={{
              color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined),
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : undefined),
            }}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="image">Image</MenuItem>
            <MenuItem value="video">Video</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Active filters indicator */}
      {hasActiveFilters && (
        <Box className="mb-4 flex items-center gap-2">
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Filters active:
          </Typography>
          {searchInput && (
            <Chip
              label={`name: "${searchInput}"`}
              size="small"
              onDelete={() => setSearchInput('')}
            />
          )}
          {statusFilter !== 'all' && (
            <Chip
              label={`status: ${statusFilter}`}
              size="small"
              onDelete={() => setStatusFilter('all')}
            />
          )}
          {categoryFilter && (
            <Chip
              label={`category: ${categoryFilter}`}
              size="small"
              onDelete={() => setCategoryFilter('')}
            />
          )}
          {typeFilter && (
            <Chip label={`type: ${typeFilter}`} size="small" onDelete={() => setTypeFilter('')} />
          )}
          <button
            onClick={handleClearFilters}
            className="ml-1 text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400"
          >
            Clear all
          </button>
        </Box>
      )}

      {/* ---- Content ---- */}
      {isLoading ? (
        <Box className="flex h-64 items-center justify-center">
          <CircularProgress sx={{ color: '#3B82F6' }} />
        </Box>
      ) : isError ? (
        <Box className="flex h-64 flex-col items-center justify-center gap-3">
          <Typography className="text-gray-500 dark:text-slate-400">
            Failed to load avatars. Please try again.
          </Typography>
          <Button onClick={() => window.location.reload()} variant="outlined" size="small">
            Retry
          </Button>
        </Box>
      ) : avatars.length === 0 ? (
        /* Empty state */
        <Box
          className="flex h-64 flex-col items-center justify-center gap-3 rounded-xl"
          sx={{
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
            border: '1px dashed',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          }}
        >
          <ImageIcon size={40} className="text-gray-300 dark:text-slate-600" />
          <Typography className="font-medium text-gray-500 dark:text-slate-400">
            {hasActiveFilters ? 'No avatars match the current filters' : 'No avatars yet'}
          </Typography>
          {!hasActiveFilters && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setUploadOpen(true)}
              sx={{
                bgcolor: '#3B82F6',
                '&:hover': { bgcolor: '#2563EB' },
                textTransform: 'none',
              }}
            >
              Upload your first avatar
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Avatar grid */}
          <Box className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {avatars.map((avatar) => (
              <AvatarCard
                key={avatar.publicId}
                avatar={avatar}
                onEdit={(a) => setEditAvatar(a)}
                onToggleStatus={handleToggleStatus}
                onDelete={(id) => setDeleteTarget(id)}
              />
            ))}
          </Box>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <Box className="mt-6 flex justify-center">
              <Pagination
                count={meta.totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined),
                  },
                  '& .MuiPaginationItem-root.Mui-selected': {
                    bgcolor: '#3B82F6',
                    color: 'white',
                  },
                }}
              />
            </Box>
          )}

          {/* Result count */}
          {meta && (
            <Typography
              variant="caption"
              className="mt-3 block text-center text-gray-400 dark:text-slate-500"
            >
              Showing {avatars.length} of {meta.total} avatar{meta.total !== 1 ? 's' : ''}
            </Typography>
          )}
        </>
      )}

      {/* ---- Upload Dialog ---- */}
      <AvatarUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={handleUpload}
        isLoading={uploadMutation.isPending}
      />

      {/* ---- Edit Dialog ---- */}
      <AvatarEditDialog
        open={Boolean(editAvatar)}
        avatar={editAvatar}
        onClose={() => setEditAvatar(null)}
        onSubmit={handleEdit}
        isLoading={updateMutation.isPending}
      />

      {/* ---- Delete Confirmation Dialog ---- */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle className="text-gray-900 dark:text-slate-100">Delete Avatar</DialogTitle>
        <DialogContent>
          <DialogContentText className="text-gray-600 dark:text-slate-400">
            This will permanently delete the avatar and remove its stored media file. This action
            cannot be undone. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ textTransform: 'none', color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
            }
            sx={{
              textTransform: 'none',
              bgcolor: '#EF4444',
              '&:hover': { bgcolor: '#DC2626' },
              fontWeight: 600,
            }}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
